import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, KeyRound, Mail, Github, XCircle, CreditCard, Sparkles, Loader2, Check } from "lucide-react";
import { api } from "../../lib/api";

export default function Settings({ user, onLogout }) {
  const [gh, setGh] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/github/status").then((r) => setGh(r.data)).catch(() => setGh({ configured: false }));
  }, []);

  const plan = user?.plan || "free";
  const PLAN_LABELS = { free: "Open Ecosystem (Free)", solo: "Solo Maintainer", education: "Developer Education" };
  const BADGE = { free: "free", solo: "solo", education: "pro" };

  const startCheckout = async (planId) => {
    setUpgrading(true);
    try {
      const res = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setUpgrading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-card border border-border p-6">
        <h3 className="font-display font-extrabold tracking-tight text-xl mb-5">Account</h3>
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 border-2 border-foreground" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-14 h-14 bg-foreground text-background flex items-center justify-center font-display font-black text-xl">
              {user?.name?.[0] || "?"}
            </div>
          )}
          <div className="font-mono">
            <div className="font-bold text-base">{user?.name}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1"><Mail size={13} /> {user?.email}</div>
          </div>
        </div>
        <button onClick={onLogout} data-testid="settings-logout-btn" className="btn btn-dark mt-6">
          <LogOut size={15} /> Sign out
        </button>
      </div>

      <div className="bg-card border border-border p-6" data-testid="settings-billing-card">
        <h3 className="font-display font-extrabold tracking-tight text-xl mb-4 flex items-center gap-2"><CreditCard size={18} /> Billing & Plan</h3>
        <div className="flex items-center justify-between border-2 border-foreground bg-muted px-4 py-3 mb-4">
          <div className="font-mono text-sm">
            Current plan: <span className="font-bold uppercase">{PLAN_LABELS[plan]}</span>
            {plan !== "free" && user?.plan_expires && (
              <div className="text-[11px] text-muted-foreground mt-0.5">renews {new Date(user.plan_expires).toLocaleDateString()}</div>
            )}
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${plan !== "free" ? "border-accent text-[hsl(42,55%,40%)]" : "border-muted-foreground text-muted-foreground"}`}>
            {BADGE[plan]}
          </span>
        </div>
        <div className="space-y-2">
          {plan === "free" && (
            <button onClick={() => startCheckout("solo")} disabled={upgrading} data-testid="settings-upgrade-solo-btn"
              className="btn btn-dark btn-block">
              {upgrading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Go Solo Maintainer — $12 / month
            </button>
          )}
          {plan !== "education" && (
            <button onClick={() => startCheckout("education")} disabled={upgrading} data-testid="settings-upgrade-btn"
              className="btn btn-primary btn-block">
              {upgrading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Upgrade to Developer Education — $49 / month
            </button>
          )}
          {plan !== "free" && (
            <button onClick={openPortal} disabled={portalLoading} data-testid="settings-manage-billing-btn"
              className="btn btn-outline btn-block">
              {portalLoading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Manage billing &amp; invoices
            </button>
          )}
          {plan === "education" && (
            <div className="font-mono text-xs text-muted-foreground text-center py-2">You're on the top self-serve tier. Need teams or SSO? <a href="mailto:sales@patchwire.pro" className="text-secondary hover:text-primary">Talk to us about Enterprise →</a></div>
          )}
          <p className="font-mono text-[11px] text-muted-foreground text-center pt-1" data-testid="settings-billing-note">
            Subscriptions renew monthly · cancel anytime from the billing portal.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border p-6" data-testid="settings-github-card">
        <h3 className="font-display font-extrabold tracking-tight text-xl mb-4 flex items-center gap-2"><Github size={18} /> GitHub delivery identity</h3>
        {gh === null ? (
          <div className="font-mono text-sm text-muted-foreground">Checking…</div>
        ) : gh.configured ? (
          <div className="flex items-center gap-3 p-4 border-2 border-foreground bg-background shadow-brutal" data-testid="github-identity-panel">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary border-2 border-foreground shrink-0">
              <Check size={14} className="text-background" strokeWidth={3} />
            </div>
            <div className="font-mono leading-tight">
              <div className="text-sm font-bold" data-testid="github-identity-login">Connected as @{gh.login}</div>
              <div className="text-xs text-muted-foreground" data-testid="github-identity-repos">
                {gh.pushable_repos}{gh.pushable_capped ? "+" : ""} {gh.pushable_repos === 1 ? "repo" : "repos"} you can push to
                {gh.source === "operator" ? " · shared operator identity" : ""}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 font-mono text-sm border-2 border-foreground bg-muted px-4 py-3">
            <XCircle size={16} className="text-primary" />
            No GitHub connected — PRs run in dry-run only. {gh.error ? `(${gh.error})` : "Sign in with GitHub to deliver fixes under your own account."}
          </div>
        )}
      </div>

      <div className="bg-card border border-border p-6">
        <h3 className="font-display font-extrabold tracking-tight text-xl mb-2 flex items-center gap-2"><KeyRound size={18} /> Telemetry access</h3>
        <p className="font-mono text-sm text-muted-foreground leading-relaxed">
          Patchwire reads public repository telemetry via the GitHub REST API and delivers fixes as Pull Requests using a
          fine-grained token (Contents + Pull requests: read/write). Auto-fork is disabled — PRs open only on repositories
          your token can push to directly; everything else stays in dry-run for review.
        </p>
      </div>
    </div>
  );
}
